import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { Tree, TreeNode } from '@shared/ui-native/Tree';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import * as olExtent from 'ol/extent';
import BaseLayer from 'ol/layer/Base';
import * as React from 'react';
import { mapWrapper } from '../../map';


export class BgMapsPanel implements IStateful<IMapPageStateMapsPanel> {
    tree: Tree = null;

    activePromises: Set<string>;
    cancelledPromises: Set<string>;

    activeLegacyPromise: JQueryPromise<any>;
    legacyIds: number[];

    constructor(public panelWrapper: HTMLElement, private treeData: Promise<ITreeData>) {
        this.activePromises = new Set();
        this.cancelledPromises = new Set();
        this.legacyIds = [];
        this.initModelsTree();
    }

    clear = () => {
        this.tree.each(c => {
            c.setChecked(0, false, false);
        }, true);
    };

    selectAll = () => {
        this.tree.each(node => {
            node.setChecked(1, false, false);
        }, true);
        setTimeout(this.renderSelectionFeedback);
    };

    getCheckedFileIds(): number[] {
        if (!this.tree) return [];

        return this.tree.getChecked()
            .filter(c => c.isFile())
            .map(c => c.id);
    }

    getCheckedHeadUuids(): string[] {
        if (!this.tree) return [];

        return this.tree.getChecked()
            .filter(c => c.isFile())
            .map(c => c.headUuid);
    }

    getCheckedFileNames(): string[] {
        if (!this.tree) return [];

        return this.tree.getChecked()
            .filter(c => c.isFile())
            .map(c => c.text);
    }

    public focusItem(idOrUuid: number | string) {
        const item = typeof idOrUuid === 'number' ? this.tree.getById(idOrUuid) : this.tree.getByUuid(idOrUuid);
        if (item) {
            item.setSelected(true, true, true);
            item.scrollTo();
        }
    }

    private async createGeoJSONLayer(node: TreeNode): Promise<BaseLayer> {
        try {
            const layer: BaseLayer = await mapWrapper.backgroundMaps.createRasterLayerForModel(node)
            return layer;
        } catch (e) {
            return null;
        }
    }

    private initModelsTree() {
        this.panelWrapper.classList.add('overlay-loading', 'no-bg');
        this.treeData.then(treeData => {
            this.tree = new Tree({
                rootEl: $('#js-app__maps-panel__tree-wrapper'),
                showRoot: false,
                showCheckboxes: true,
                propagateCheckbox: 'first_level_files',
                selectionCheckbox: false,
                hideNonPresentable: true,
                storageKey: 'map-bg-maps-panel',
                hideEmptyFoldersAlways: true,
                showContextMenuInfo: true,
                childFilter: (c) => {
                    if (c.tolerance) return true;
                    return kuuraModel.isMap(c);
                }
            });

            this.tree.on('check', (async (_e, data) => {
                data.changed.forEach(async node => {
                    if (node.isFolder()) {
                        return;
                    }

                    if (node.isChecked()) {
                        node.setLoading(true);

                        this.activePromises.add(node.uuid);

                        const layer: BaseLayer = await this.createGeoJSONLayer(node);
                        this.activePromises.delete(node.uuid);

                        /* Check if the loading was cancelled when
                           fetching the geojson and creating the layer.
                           If it was do not load the drawing
                        */

                        node.setLoading(false);

                        if (this.cancelledPromises.has(node.uuid)) {
                            this.cancelledPromises.delete(node.uuid);
                            return;
                        }

                        if (layer) {
                            mapWrapper.addLayer(layer, 1, node.uuid);
                        } else {
                            node.axiosState.error = 'Error';
                            node.view?.renderError();
                            node.setChecked(0, true, false);
                        }

                    } else {
                        if (this.activePromises.has(node.uuid)) {
                            this.cancelledPromises.add(node.uuid);
                        }

                        mapWrapper.removeLayerById(node.uuid);
                        mapWrapper.backgroundMaps.removeDrawing(node.uuid);
                    }
                })

                this.renderSelectionFeedback();
            }));

            this.tree.on('select', (e, data) => {
                if ((data.node as TreeNode).isFolder()) {
                    return;
                }
                if ((data.node as TreeNode).isChecked()) {

                    const extent: olExtent.Extent = mapWrapper.backgroundMaps.getExtentForDrawing(data.node.uuid);
                    if (!extent) return;

                    mapWrapper.fitExtent(extent);
                }
            });

            this.tree.on('render', () => {
                this.panelWrapper.classList.remove('overlay-loading');

                // no items
                const count = this.tree.getChildren(true).filter(c => c.isFile()).length;
                if (count === 0) {
                    this.panelWrapper.querySelector('#js-app__maps-panel__tree-wrapper').innerHTML =
                        `<div class="panel-no-items"><i class="iki iki-designs"></i><br>${kuura.getMessage('js_map.background_maps_empty')}</div>`;
                    return;
                }

                // check maps from initial state
                this.tree.checkIds(mapWrapper.backgroundMaps.getModelIds());

                this.tree.calculateCheckboxes();

                if (this.stateful.queuedRestoreState) {
                    this.stateful.restoreState(this.stateful.queuedRestoreState);
                }

                setTimeout(() => this.renderSelectionFeedback(), 50);
            });

            this.tree.initAndRender(treeData, true);
        });
    }

    private renderSelectionFeedback = () => {
        const selectionEl = this.panelWrapper.querySelector('#map-2d-maps-panel-wrapper .tree-selection-feedback-wrapper');
        if (this.tree && selectionEl) {
            const selection = this.tree.getCheckedAndUncheckedFileIds();
            ReactUtils.safeRender(<TreeSelectionFeedback
                checkedCount={selection.checkedIds.length}
                loadedItemsCount={selection.checkedIds.length}
                totalItemsCount={selection.checkedIds.length + selection.uncheckedIds.length}
                onClearSelection={this.clear}
                onSelectAll={this.selectAll}
                countTooltip={kuura.getMessage('js_map.bgmaps.selected_count_tooltip')}
            />, selectionEl);
        }
    }

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            headUuids: [],
            fileIds: [],
            fileNames: [],
            rasterizedModelIds: [],
            layerOpacity: 100
        },

        getCurrentState: (): IMapPageStateMapsPanel => {
            return {
                headUuids: this.getCheckedHeadUuids() || [],
                fileIds: this.getCheckedFileIds() || [],
                fileNames: this.getCheckedFileNames() || [],
                rasterizedModelIds: Array.from(mapWrapper.backgroundMaps.getRasterizedModelIds()),
                layerOpacity: mapWrapper.backgroundMaps.layerOpacity
            };
        },

        restoreDefaultState: () => {
            this.stateful.restoreState(this.stateful.defaultState);
        },

        restoreState: (state: IMapPageStateMapsPanel): void => {
            if (!state) return null;

            if (!this.tree) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state)) as IMapPageStateMapsPanel;
                return;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'rasterizedModelId')) {
                mapWrapper.backgroundMaps.getRasterizedModelIds().clear();
                mapWrapper.backgroundMaps.toggleRasterizedModelId(state.rasterizedModelId);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'rasterizedModelIds')) {
                if (Array.isArray(state.rasterizedModelIds)) {
                    mapWrapper.backgroundMaps.getRasterizedModelIds().clear();
                    state.rasterizedModelIds.forEach(id => {
                        mapWrapper.backgroundMaps.toggleRasterizedModelId(id);
                    });
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'headUuids') ||
                PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'fileIds')) {
                if (Array.isArray(state.headUuids) || Array.isArray(state.fileIds)) {
                    this.tree.checkIdsOrHeadUuids(state.fileIds, state.headUuids);
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'layerOpacity')) {
                mapWrapper.backgroundMaps.layerOpacity = state.layerOpacity;
            }

            this.stateful.queuedRestoreState = null;
        }
    };
}

